import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { SubmitButton } from "@/components/ui/button";
import {
  OrganizationPayloadSchema,
  organizationPayloadSchema,
  useOrganization,
  useOrganizationEdit,
} from "@/api/organization";
import { Loading } from "@/components/ui/loading";
import { OrganizationFormFields } from "./OrganizationFormFields";
import { FormContainer } from "@/components/form/form-container";
import { Anchor } from "@/components/ui/anchor";
import { FormInfo } from "@/components/ui/form-info";

export function OrganizationEditForm({ id }: { id: string }) {
  const { data: organization, isPending: isLoadingData } = useOrganization({
    id,
  });
  const form = useForm<OrganizationPayloadSchema>({
    resolver: zodResolver(organizationPayloadSchema),
    defaultValues: {
      companyCode: organization?.companyCode,
      companyName: organization?.companyName,
      department: organization?.department,
      noTelp: organization?.noTelp,
      email: organization?.email,
      address: organization?.address,
    },
    values: organization,
  });
  const { mutate, isPending } = useOrganizationEdit();
  const navigate = useNavigate();

  const handleSubmit = (values: OrganizationPayloadSchema) => {
    mutate(
      { id: id, data: values },
      {
        onSuccess: () => {
          swalSuccess
            .fire({
              text: "Organization has been updated",
            })
            .then(() => navigate("/master-data/organization"));
        },
        onError: (error) => {
          swalError.fire({
            text: error.message,
          });
        },
      },
    );
  };

  if (isLoadingData) {
    return <Loading />;
  }

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <FormInfo
        label="Terms"
        values={["The maximum data inputted in each field is 255 characters"]}
      />
      <OrganizationFormFields />
      <div className="ml-auto flex gap-x-2">
        <Anchor href="/master-data/organization" mood="axiata-blue-outline">
          Cancel
        </Anchor>
        <SubmitButton loading={isPending} />
      </div>
    </FormContainer>
  );
}
